import React, {useContext, useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {FormattedMessage} from "react-intl";

// Material UI
import {Grid} from "@mui/material";

// Components
import CustomButton from "components/CustomButton";
import MyTasks from "components/WMS/Mobile/MyTasks";
import PageTitle from "components/WMS/Mobile/PageTitle";
import Popup from "components/WMS/Mobile/Popup";
import SelfTaskAffectation from "./components/SelfTaskAffectation";
import WmsMobileLayout from "../wmsMobileLayout";
import PickingToastManager, {ToastType} from "./components/PickingToastManager";

// API
import {getTasks, patchTaskStarted} from "api/wms/tasks/list";
import {getWMSContainerWithCodeBar, patchWMSContainerMemberWithCodebar} from "api/wms/wmsContainers/wmsContainers";

// Contexts
import {BarCodeScannerContext} from "contexts/barCodeScannerContext";
import {WMSContext} from "../context/WMSContext";

// Utils
import {WMSRoutes} from "utils/WMSRoutes";

// Assets
import Shelf from "assets/img/wms/shelf.svg";
import Trolley from "assets/img/wms/trolley.svg";
import NoRetrievedTaskToast from "./components/NoRetrievedTaskToast";
import {WmsContainerType} from "../../../utils/WMSContainerType";

function Picking() {
    const {
        currentInventory,
        currentMember,
        setCurrentTask,
    } = useContext(WMSContext)

    const {scannedString, setEnableScan, resetScannedString} = useContext(BarCodeScannerContext)

    const [openAffectationModal, setOpenAffectationModal] = useState(false)
    const [openTrolleyModal, setOpenTrolleyModal] = useState(false)
    const [tasks, setTasks] = useState()
    const [retrievedTask, setRetrievedTask] = useState({})
    const [selectedTask, setSelectedTask] = useState()
    const [noRetrievedTaskToast, setNoRetrievedTaskToast] = useState(false)

    const location = useLocation()
    const history = useHistory();

    const [toast, setToast] = useState({
        open: false,
        type: 0,
    })

    const handleOpenAffectationModal = () => {
        setOpenAffectationModal(true)
    }

    const handleInit = () => {
        getTasks(currentInventory, currentMember.id).then((response) => {
            setTasks(response);
            setEnableScan(true)
        })
    }

    const handleCloseAffectationModal = () => {
        setOpenAffectationModal(false)
        setRetrievedTask({})
        handleInit()
    }

    const handleOpenTrolleyModal = () => {
        handleCloseAffectationModal()
        setEnableScan(true)
        setOpenTrolleyModal(true)
    }

    const handleCloseTrolleyModal = () => {
        setOpenTrolleyModal(false)
        handleCloseAffectationModal()
    }

    const handleNoRetrievedTaskToast = () => {
        setNoRetrievedTaskToast(false)
    }

    function handelSelectedTaskOpenTrolley(task) {
        setSelectedTask(task)
        setOpenTrolleyModal(true)
    }

    // on scan
    useEffect(() => {
        if (currentMember && selectedTask && openTrolleyModal && scannedString && scannedString.length > 0) {
            setEnableScan(false)
            getWMSContainerWithCodeBar(scannedString, true).then((wmsContainer) => {
                if (!trolleyIsOk(wmsContainer)) {
                    return;
                }

                patchWMSContainerMemberWithCodebar(scannedString, currentMember).then((response) => {
                    patchTaskStarted(selectedTask, currentMember).then((response) => {
                        resetScannedString()
                        setCurrentTask(selectedTask);
                        history.push(`${WMSRoutes.PICKING}/${selectedTask}/${scannedString}`);
                    })
                })
            }).catch(() => {
                setToast({
                    open: true,
                    type: ToastType.TROLLEY_NOT_FOUND
                })
            }).finally(() => {
                setEnableScan(true);
                resetScannedString();
            });
        }
    }, [scannedString]);

    useEffect(() => {
        if (Object.keys(retrievedTask).length > 0) {
            setSelectedTask(retrievedTask)
            setOpenAffectationModal(true)
        } else if(null === retrievedTask){
             setNoRetrievedTaskToast(true)
        }
    }, [retrievedTask]);

    useEffect(() => {
        if (currentInventory) {
            handleInit()
        }
    }, [currentInventory]);

    // When we came from the MyTasks component with a selected task we have a location.state
    useEffect(() => {
        if (location.state?.task) {
            handelSelectedTaskOpenTrolley(location.state.task)
        }
    }, [location.state]);

    function closeToast() {
        setToast({
            open: false,
            type: 0,
        })
    }

    function trolleyIsOk(wmsContainer) {
        if (!wmsContainer) {
            return false;
        }

        if (wmsContainer.type !== WmsContainerType.TROLLEY) {
            setToast({
                open: true,
                type: ToastType.TROLLEY_NOT_FOUND
            })

            return false
        }

        if (isTrolleyAlreadyTaken(wmsContainer)) {
            setToast({
                open: true,
                type: ToastType.TROLLEY_ALREADY_TAKEN
            })

            return false;
        }

        if (wmsContainer.shipments.length > 0) {
            setToast({
                open: true,
                type: ToastType.TROLLEY_CONTAIN_SHIPMENTS
            })

            return false;
        }

        return true;
    }

    function isTrolleyAlreadyTaken(trolley) {
        if (!trolley.member) {
            return false;
        }

        return trolley.member.id !== currentMember.id;
    }

    return(
        <WmsMobileLayout className="wms-test-component">
            <WmsMobileLayout.LeftHeader>

            </WmsMobileLayout.LeftHeader>
            <WmsMobileLayout.RightHeader>

            </WmsMobileLayout.RightHeader>

            <WmsMobileLayout.Content overflow={'unset'}>
                <NoRetrievedTaskToast
                    open={noRetrievedTaskToast}
                    handleClose={handleNoRetrievedTaskToast}
                />
                <Popup
                    open={openAffectationModal}
                    onClose={handleCloseAffectationModal}
                    title={
                        <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.title"}/>
                    }
                    subtitle={
                        <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.description"}/>
                    }
                    icon={<img src={Shelf} alt={"Shelf"}/>}
                >
                    <Grid container>
                        <Grid container item direction={"column"}>
                            <CustomButton
                                fullWidth
                                style={{marginBottom: "16px"}}
                                onClick={handleOpenTrolleyModal}
                            >
                                <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.button"}/>
                            </CustomButton>
                            <CustomButton
                                onClick={handleCloseAffectationModal}
                                color={"default"}
                                fullWidth
                            >
                                <FormattedMessage id={"wms.myPickingMissions.selfAffectationMission.cancel"}/>
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Popup>
                <Popup
                    open={openTrolleyModal}
                    onClose={handleCloseTrolleyModal}
                    title={
                        <FormattedMessage id={"wms.myPickingMissions.scanTrolley"}/>
                    }
                    subtitle={
                        <FormattedMessage id={"wms.myPickingMissions.scanTrolley.description"}/>
                    }
                    icon={<img src={Trolley} alt={"Trolley"}/>}
                />

                <PageTitle
                    title={<FormattedMessage id={"wms.myPickingMissions.title"} />}
                    subtitle={<FormattedMessage id={"wms.myPickingMission.missionSubtitle"} />}
                />

                {tasks && Array.isArray(tasks) && tasks.length > 0 && (
                    <MyTasks
                        passedTask={tasks}

                    />
                )}

                <SelfTaskAffectation
                    setRetrievedTask={setRetrievedTask}
                    setOpenNoTaskToast={setNoRetrievedTaskToast}
                />

                <PickingToastManager
                    open={toast.open}
                    type={toast.type}
                    handleClose={closeToast}
                />
            </WmsMobileLayout.Content>
        </WmsMobileLayout>
    )
}

export default Picking;
